/**
 * Router. Three route trees:
 *
 *   /              public — Landing, Login, Register
 *   /pending       authed but inactive — pending screen
 *   /app/*         authed AND active — full app (Dashboard, Positions, …)
 *   /app/admin/*   role='admin' only
 *
 * ProtectedRoute owns the gating logic. Routes here just declare structure.
 */
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'

import Landing from '@/pages/public/Landing'
import Login from '@/pages/public/Login'
import Register from '@/pages/public/Register'
import Pending from '@/pages/public/Pending'
import ForgotPassword from '@/pages/public/ForgotPassword'
import ResetPassword from '@/pages/public/ResetPassword'
import { TradeShare } from '@/pages/share/TradeShare'
import { SignalShare } from '@/pages/share/SignalShare'
import { CoinShare } from '@/pages/share/CoinShare'
import { DrillEvent, DrillHome } from '@/pages/drill/Drill'

import Dashboard from '@/pages/app/Dashboard'
import Discovery from '@/pages/app/Discovery'
import Events from '@/pages/app/Events'
import History from '@/pages/app/History'
import Positions from '@/pages/app/Positions'
import Signals from '@/pages/app/Signals'
import MySettings from '@/pages/app/Settings'
import Slots from '@/pages/app/Slots'
import Analytics from '@/pages/app/Analytics'
import Catalog from '@/pages/app/Catalog'
import PulsarLab from '@/pages/app/PulsarLab'
import PulsarLabStrategy from '@/pages/app/PulsarLabStrategy'
import Screeners from '@/pages/app/Screeners'
import ScreenerDetail from '@/pages/app/ScreenerDetail'
import ScreenerSetups from '@/pages/app/ScreenerSetups'
import Market from '@/pages/app/Market'
import FriendTrades from '@/pages/app/FriendTrades'
import Atlas from '@/pages/app/Atlas'
import Symbols from '@/pages/app/Symbols'
import AdminSettings from '@/pages/admin/Settings'
import AdminAssignments from '@/pages/admin/Assignments'
import AdminUsers from '@/pages/admin/Users'

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/share/trade/:token" element={<TradeShare />} />
        <Route path="/share/signal/:token" element={<SignalShare />} />
        <Route path="/share/coin/:token" element={<CoinShare />} />
        {/* PULSE-476: тренажёр решений — публичный по токену участника */}
        <Route path="/drill/:token" element={<DrillHome />} />
        <Route path="/drill/:token/:ord" element={<DrillEvent />} />

        {/* Pending — authed but inactive */}
        <Route element={<ProtectedRoute allowPending />}>
          <Route path="/pending" element={<Pending />} />
        </Route>

        {/* App — authed + active */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route path="/app" element={<Dashboard />} />
            <Route path="/app/positions" element={<Positions />} />
            <Route path="/app/signals" element={<Signals />} />
            <Route path="/app/analytics" element={<Analytics />} />
            <Route path="/app/catalog" element={<Catalog />} />
            <Route path="/app/lab" element={<PulsarLab />} />
            <Route path="/app/lab/:id" element={<PulsarLabStrategy />} />
            <Route path="/app/discovery" element={<Discovery />} />
            <Route path="/app/events" element={<Events />} />
            <Route path="/app/symbols" element={<Symbols />} />
            <Route path="/app/history" element={<History />} />
            <Route path="/app/screeners" element={<Screeners />} />
            <Route path="/app/screeners/setups" element={<ScreenerSetups />} />
            <Route path="/app/screeners/:id" element={<ScreenerDetail />} />
            <Route path="/app/settings" element={<MySettings />} />
          </Route>
        </Route>

        {/* Admin */}
        <Route element={<ProtectedRoute requireAdmin />}>
          <Route element={<AppShell />}>
            <Route path="/app/slots" element={<Slots />} />
            <Route path="/app/admin/users" element={<AdminUsers />} />
            <Route path="/app/admin/assignments" element={<AdminAssignments />} />
            <Route path="/app/market" element={<Market />} />
            <Route path="/app/friend-trades" element={<FriendTrades />} />
            <Route path="/app/atlas" element={<Atlas />} />
            <Route path="/app/admin/settings" element={<AdminSettings />} />
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
